import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, CalendarCheckIcon, MessageCircleOffIcon, RefreshCwIcon, LockIcon } from 'lucide-react';
const trustPills = [{
  Icon: CalendarCheckIcon,
  label: '7 días completos'
}, {
  Icon: MessageCircleOffIcon,
  label: 'Sin preguntas'
}, {
  Icon: RefreshCwIcon,
  label: 'Reembolso 100%'
}];
export function GuaranteeSection() {
  return <section className="w-full bg-bg-main py-24 px-4 relative overflow-hidden" data-id="element-316">
      {/* Decorative blobs */}
      <div aria-hidden className="absolute top-1/3 -left-20 w-[400px] h-[400px] bg-primary/5 rounded-full blur-3xl pointer-events-none" data-id="element-317" />
      <div aria-hidden className="absolute bottom-1/3 -right-20 w-[400px] h-[400px] bg-cta-green/5 rounded-full blur-3xl pointer-events-none" data-id="element-318" />

      <div className="max-w-4xl mx-auto relative z-10" data-id="element-319">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.6
      }} className="relative bg-bg-alt border border-primary/30 shadow-[0_0_50px_rgba(59,130,246,0.12)] rounded-3xl p-8 sm:p-14 text-center overflow-hidden" data-id="element-320">
          {/* Top accent gradient bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent" data-id="element-321" />

          {/* Subtle background glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1/2 bg-primary/5 blur-[60px] pointer-events-none" data-id="element-322" />
          <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 w-72 h-72 bg-cta-green/5 rounded-full blur-3xl pointer-events-none" data-id="element-323" />

          {/* Section badge */}
          <div className="relative z-10 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6" data-id="element-324">
            <LockIcon className="w-3.5 h-3.5 text-primary" data-id="element-325" />
            <span className="text-xs font-semibold text-primary tracking-wider uppercase" data-id="element-326">
              Compra 100% Protegida
            </span>
          </div>

          {/* Shield icon with rings */}
          <div className="relative w-28 h-28 mx-auto mb-8" data-id="element-327">
            {/* Pulse rings */}
            <motion.div aria-hidden className="absolute inset-0 rounded-full border border-primary/30" animate={{
            scale: [1, 1.4, 1.4],
            opacity: [0.6, 0, 0]
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeOut'
          }} data-id="element-328" />
            <motion.div aria-hidden className="absolute inset-0 rounded-full border border-primary/30" animate={{
            scale: [1, 1.4, 1.4],
            opacity: [0.6, 0, 0]
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeOut',
            delay: 1.25
          }} data-id="element-329" />
            <div className="relative w-28 h-28 bg-gradient-to-br from-primary/25 to-primary/5 border border-primary/40 rounded-full flex items-center justify-center shadow-inner" data-id="element-330">
              <ShieldCheckIcon className="w-14 h-14 text-primary drop-shadow-[0_0_15px_rgba(59,130,246,0.6)]" strokeWidth={2} data-id="element-331" />
            </div>
          </div>

          {/* Days badge */}
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-full bg-cta-green/10 border border-cta-green/30" data-id="element-332">
            <span className="w-1.5 h-1.5 rounded-full bg-cta-green animate-pulse" data-id="element-333" />
            <span className="text-xs font-bold text-cta-green tracking-wide uppercase" data-id="element-334">
              Garantía de 7 días
            </span>
          </div>

          <h2 className="text-3xl sm:text-5xl font-black text-text-main mb-5 tracking-tight leading-tight" data-id="element-335">
            Recupera tu tiempo
            <br className="hidden sm:block" data-id="element-336" />
            <span className="bg-gradient-to-r from-primary to-cyan-400 bg-clip-text text-transparent" data-id="element-337">
              {' '}
              o te reembolsamos.
            </span>
          </h2>

          <p className="text-text-sec text-base sm:text-xl leading-relaxed font-medium max-w-2xl mx-auto mb-8" data-id="element-338">
            Estamos tan seguros del impacto de este sistema que te damos{' '}
            <span className="text-primary font-bold" data-id="element-339">7 días para probarlo</span>
            . Si no sientes que tu trabajo como docente es más fácil y
            organizado, te devolvemos cada centavo.{' '}
            <span className="text-text-main font-bold" data-id="element-340">Sin preguntas.</span>
          </p>

          {/* Trust pills */}
          <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3" data-id="element-341">
            {trustPills.map(({
            Icon,
            label
          }, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.2 + i * 0.1
          }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-bg-dark/50 border border-border-light text-text-main text-sm font-semibold backdrop-blur-sm" data-id="element-342">
                <Icon className="w-4 h-4 text-primary" strokeWidth={2.5} data-id="element-343" />
                <span data-id="element-344">{label}</span>
              </motion.div>)}
          </div>

          {/* Signature-style footer line */}
          <div className="mt-10 pt-6 border-t border-border-light/60 flex items-center justify-center gap-2 text-text-sec text-xs sm:text-sm" data-id="element-345">
            <ShieldCheckIcon className="w-4 h-4 text-primary" data-id="element-346" />
            <span data-id="element-347">Riesgo cero — la garantía corre por nuestra cuenta.</span>
          </div>
        </motion.div>
      </div>
    </section>;
}